import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import {Injectable} from "@angular/core";
import {DashboardService} from "../../../worklist/services/dashboard.service";
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from "@angular/router";
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import {LogService} from "@ispm/core/services/log.service";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {catchError, mergeMap} from "rxjs/operators";
import {DiagnosticOrderService} from "@ispm/test/services/diagnostic-order.service";
import {NavigationConfigService} from "@ispm/navigation/services/navigationConfig.service";
import {WorkflowService} from "@ispm/workflow/services/workflow.service";
import {ApplicationConfigService} from "@ispm/navigation/services/applicationConfig.service";
import {of} from 'rxjs';
import {UserActionCreatorService} from "@ispm/user/services/user-action-creator.service";
import {CryptoService} from "@ispm/migration/services/crypto.service";
import {EndpointService} from "@ispm/shared/services/endpoint.service";
import { LoginAuthService } from "../../../authorization/services/login-auth.service";
import { TranslationHandlerService } from "@ispm/core/services/translation-handler.service";

@Injectable({providedIn: 'root'})
export class getCheckAuthLevel {
  constructor(private http: HttpInterceptorService) {
  }

  resolve() {
    let url = new URL(window.location.href);
    let searchParams = new URLSearchParams(url.search);
    let twoFactorEnabled = searchParams.get('twoFactorEnabled');
    return new Promise(resolve => {
      this.http.get('/auth/checkAuthLevel', twoFactorEnabled)
        .toPromise()
        .then(url => {
          if (url.body) {
            window.location = url.body;
          } else {
            resolve(true);
          }
        })
    })
  }
}

@Injectable({providedIn: 'root'})
export class getClinicalUsageLogin {
  constructor(private dashboardService: DashboardService) {
  }

  resolve() {
    return this.dashboardService.getClinicalUsageMessage();
  }
}

@Injectable({providedIn: 'root'})
export class getVersion {
  constructor(private http: HttpInterceptorService) {
  }

  resolve() {
    return this.http
      .get('/app/version')
      .toPromise()
      .then(res => {
        return res.body
      })
      .catch(err => {
        console.log(err);
        return "";
      });
  }
}

@Injectable({providedIn: 'root'})
export class getForgottenPasswordUrl {
  constructor(private http: HttpInterceptorService) {
  }

  resolve() {
    return this.http
      .get('/app/forgottenPassword')
      .toPromise()
      .then(res => {
        return res.body
      })
      .catch(err => {
        console.log(err);
        return "";
      });
  }
}

@Injectable({providedIn: 'root'})
export class redirectToDashboard implements CanActivate {
  constructor(private http: HttpInterceptorService,
              private authService: AuthenticationService,
              private loginAuthService: LoginAuthService) {
  }

  canActivate(activatedRouteSnapshot: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
    let user = this.authService.getUser();
    let ttl = document.cookie.match(/^(.*;)?\s*LC_[-.0-9a-zA-Z]+_[a-zA-Z0-9]+\s*=\s*[^;]+(.*)?$/);

    if (activatedRouteSnapshot.queryParams.organization) {
      sessionStorage.setItem("custom_login", activatedRouteSnapshot.queryParams.organization);
    }

    if (ttl && user) {
      return this.authService.checkSession().then((data) => {
        this.loginAuthService.redirectionToDashboard(user);
        return false;
      }).catch(() => {
        this.authService.setCurrentUser(null);
      });
    }
    return true;
  }
}

@Injectable({ providedIn: 'root' })
export class samlRedirect {
  constructor(private logService: LogService,
              private authenticationService: AuthenticationService,
              private coreActionCreatorService: CoreActionCreatorService,
              private router: Router,
              private routerManagerService: RouterManagerService,
              private userActionCreatorService: UserActionCreatorService,
              private redirectState: RedirectState) {}

  resolve() {
    const transactionId = this.routerManagerService.queryParameters.transactionId || this.logService.generateTransactionId();
    if (this.routerManagerService.queryParameters.triggeredBy === 'renewToken') {
      this.coreActionCreatorService.skipUserInteractionUpdate(true);
    }

    this.logService.info('[app.js] Before Saml Login', '[app.js]', transactionId);

    this.authenticationService.samlLogin(this.routerManagerService.queryParameters.assertion)
      .pipe(
        mergeMap((response) => {
          this.logService.info('[app.js] Saml Login Succeed', '[app.js]', transactionId);
          const orgObj = {
            orgId: this.routerManagerService.queryParameters.orgId,
            autoLoadOnSamlLogin: this.routerManagerService.queryParameters.autoLoadOnSamlLogin
          };
          return this.redirectState.getRedirectState(this.routerManagerService.queryParameters.patientId, this.routerManagerService.queryParameters.appname, orgObj, transactionId);
        }),
        catchError((error) => {
          console.error(`[App.js] :: samlLogin error: ${error}`);
          return of({ path: 'generic-error', params: { errorType: 'LoginFailed' } });
        })
      ).subscribe((routeState) => {
      const route = this.redirectState.configureRoute(routeState);
      this.userActionCreatorService.setOrganizationConfiguration();
      this.router.navigate([route.url],{queryParams: route.queryParams});
    });
  }

}

@Injectable({providedIn:'root'})
export class oauth2LoginRedirect {
  constructor(private logService: LogService,
              private authenticationService: AuthenticationService,
              private coreActionCreatorService: CoreActionCreatorService,
              private routerManagerService: RouterManagerService,
              private redirectState: RedirectState,
              private userActionCreatorService: UserActionCreatorService,
              private router: Router) {
  }

  resolve(){
    const transactionId = this.logService.generateTransactionId();

    this.authenticationService.oauth2Login(this.routerManagerService.queryParameters.code, this.routerManagerService.queryParameters.state)
      .pipe(
        mergeMap((response: any) => {
          this.logService.info('[app.js] Oauth2Login Succeed', '[app.js]', transactionId);
          if (response.body.userData.errorType) {
            return of({path: 'generic-error', params: {errorType: response.body.userData.errorType}});
          }
          return this.redirectState.getRedirectState(response.body.userData.patientId, response.body.userData.appName, response.body.orgObj, transactionId)
        }),
        catchError((error) => {
          console.error(`[App.js] :: oauth2Login error: ${error}`);
          return of({path: 'generic-error', params: {errorType: 'LoginFailed'}});
        })
      ).subscribe((routeState) => {
      const route = this.redirectState.configureRoute(routeState);
      this.userActionCreatorService.setOrganizationConfiguration();
      this.router.navigate([route.url],{queryParams: route.queryParams});
    });
  }

}

@Injectable({providedIn:'root'})
export class smartRedirect {
  constructor(private logService: LogService,
              private authenticationService: AuthenticationService,
              private coreActionCreatorService: CoreActionCreatorService,
              private routerManagerService: RouterManagerService,
              private redirectState: RedirectState,
              private userActionCreatorService: UserActionCreatorService,
              private router: Router) {
  }

  resolve(){
    const transactionId = this.logService.generateTransactionId();
    this.logService.info('[app.js] smart/launch ', '[app.js]', transactionId);
    this.authenticationService.smartLogin(this.routerManagerService.queryParameters.jwe)
      .pipe(
        mergeMap((response: any) => {
          return this.redirectState.getRedirectState(response.body.patientId, this.routerManagerService.queryParameters.appname, response.body.orgObj, transactionId);
        }),
        catchError((error) => {
          console.log('error smart ' + error);
          this.logService.info('[App.js] :: Smart on FHIR integration error: ' + error, '[app.js]', transactionId);

          if (error.error && error.error.errorType) {
            return of({path: 'generic-error', params: {errorType: error.error.errorType}});
          }

          return of({path: 'generic-error', params: {errorType: 'LoginFailed'}});
        })
    ).subscribe((routeState) => {
      const route = this.redirectState.configureRoute(routeState);
      this.userActionCreatorService.setOrganizationConfiguration();
      this.router.navigate([route.url],{queryParams: route.queryParams});
    });
  }

}


@Injectable({ providedIn: 'root' })
export class RedirectState {
  private patientId;
  private appName;
  private orgObj;
  private transactionId;

  constructor(private routerManagerService: RouterManagerService,
              private diagnosticOrderService: DiagnosticOrderService,
              private logService: LogService,
              private workflowService: WorkflowService,
              private applicationConfigService: ApplicationConfigService,
              private authenticationService: AuthenticationService,
              private navigationConfigService: NavigationConfigService) {

  }

  getRedirectState(patientId, appName, orgObj, transactionId) {
    this.patientId = patientId;
    this.appName = appName;
    this.orgObj = orgObj;
    this.transactionId = transactionId;
    const testNumber = this.routerManagerService.queryParameters.testNumber;
    const externalaction = this.routerManagerService.queryParameters.externalaction;
    const payload = this.routerManagerService.queryParameters.payload;
    let appConfiguration = undefined;

    if (externalaction && payload) {
      return {path: 'externalaction/:action', params: {action: externalaction, payload: payload}};
    }

    if (testNumber) {
      return this.getRedirectStateByTestNumber().then(res => {
        return res;
      });
    } else {
      if (this.appName) {
        this.logService.info(`[app.js] Getting All App Configurations started`, '[app.js]', this.transactionId);
        return this.getApplicationConfiguration()
          .then(app => {
            appConfiguration = app;
            if (!this.patientId || this.patientId === 'CURATOR') {
              return this.getRedirectStateWithNoPatient(appConfiguration);
            } else {
              return this.getRedirectStateWithPatient(appConfiguration);
            }
          })
          .catch(err => {
            return ({path: 'generic-error', params: {errorType: 'LoginFailed'}});
          });
      } else if (!this.patientId || this.patientId === 'CURATOR') {
        return new Promise((resolve) => {
          resolve(this.getRedirectStateWithNoPatient(appConfiguration));
        });
      } else {
        return this.getRedirectStateWithPatient(appConfiguration);
      }
    }
  }

  getRedirectStateByTestNumber() {
    this.logService.info(`[app.js] Getting Diagnostic Order Started`, '[app.js]', this.transactionId);
    return this.diagnosticOrderService.getDiagnosticOrderByTestNumber(this.routerManagerService.queryParameters.testNumber).toPromise()
      .then(diagnosticOrder => {
        this.logService.info(`[app.js] Getting Diagnostic Order Done`, '[app.js]', this.transactionId);
        const patientId = diagnosticOrder.subject;
        const testId = diagnosticOrder.workflowRequest;

        this.logService.info(`[app.js] Getting WorkflowService By Id started`, '[app.js]', this.transactionId);
        return this.workflowService.getWorklfowById(testId)
          .then(workflow => {
            this.logService.info(`[app.js] Getting WorkflowService By Id done`, '[app.js]', this.transactionId);
            const lastRevision = workflow.body.revisions[workflow.body.revisions.length - 1].id;
            const revisionId = workflow.body.activeRevision === 'None' ? lastRevision : workflow.body.activeRevision;
            this.logService.info(`[app.js] Before redirecting to Pathologist/:patientId/:testId/:revisionId`, '[app.js]', this.transactionId);
            return {
              path: 'Pathologist/:patientId/:testId/:revisionId', params: {
                patientId: patientId,
                testId: testId,
                revisionId: revisionId
              }
            };
          });
      });
  }

  getApplicationConfiguration() {
    this.logService.info(`[app.js] Getting All App Configurations started`, '[app.js]', this.transactionId);
    return this.applicationConfigService.getAllAppConfigurations().then(apps => {
      this.logService.info(`[app.js] Getting All App Configurations Done`, '[app.js]', this.transactionId);
      return apps.find(val => val.alias === this.appName);
    });
  }

  getRedirectStateWithNoPatient(appConfiguration) {
    let dashboardState = {path: 'dashboard', params: {transactionId: this.transactionId}};
    let emrIntegration = this.routerManagerService.queryParameters?.EMRIntegration;

    if (emrIntegration) {
      dashboardState.params['EMRIntegration'] = emrIntegration;
    }

    if (!this.appName || !appConfiguration) {
      return dashboardState;
    } else if (appConfiguration.enable) {
      if (Array.isArray(appConfiguration.items) && appConfiguration.items.length > 0) {
        let redirectUrl = appConfiguration.items[0].redirectUrl;
        const stateRedirectObjectName = this.navigationConfigService.getStateNameFromUri(redirectUrl);
        const stateRedirectParams = this.navigationConfigService.getUrlQueryParams(redirectUrl);
        dashboardState.path = stateRedirectObjectName;
        Object.assign(dashboardState.params, stateRedirectParams);
      }
      return dashboardState;
    } else {
      return ({path: 'generic-error', params: {errorType: 'PERMISSION_DENIED'}});
    }
  }

  getRedirectStateWithPatient(appConfiguration) {
    try {
      let shouldRedirect;
      if (Array.isArray(this.orgObj.autoLoadOnSamlLogin)) {
        shouldRedirect = this.orgObj.autoLoadOnSamlLogin.includes(this.appName);
      } else
        shouldRedirect = this.orgObj.autoLoadOnSamlLogin === true;

      if (shouldRedirect) {
        return this.getRedirectStateByTestOrDiagnostic();
      } else {
        return this.getRedirectStateByPatient(appConfiguration);
      }
    } catch(err)  {
      console.error(`[App.js] - getRedirectState :: Get patient error: ${JSON.stringify(err)}`);
      this.logService.error(`[App.js] - getRedirectState :: Get patient error: ${JSON.stringify(err)}`, '[app.js]', this.transactionId);
      this.authenticationService.logout({refresh: true, skipNavigationToLogin: true});
      return {path: 'generic-error', params: {errorType: 'PatientNotExist'}};
    }
  }

  getRedirectStateByTestOrDiagnostic() {
    this.logService.info(`[app.js] Getting Patient Analysis Started`, '[app.js]', this.transactionId);
    return this.workflowService.getPatientAnalyses({patientId: this.patientId})
      .then(res => {
        this.logService.info(`[app.js] Getting Patient Analysis Done`, '[app.js]', this.transactionId);
        let allTests = res.body.allTests;
        if (allTests && allTests.length > 0 && allTests[allTests.length - 1].id) {
          let revisions = allTests[allTests.length - 1].revisions;
          if (revisions[revisions.length - 1].id) {
            this.logService.info(`[app.js] Before redirecting to Pathologist/:patientId/:testId/:revisionId`, '[app.js]', this.transactionId);
            return {
              path: 'Pathologist/:patientId/:testId/:revisionId', params: {
                patientId: this.patientId,
                testId: allTests[allTests.length - 1].id,
                revisionId: revisions[revisions.length - 1].id
              }
            };
          }
        }

        this.logService.info(`[app.js] Getting Diagnostic Order Templates started`, '[app.js]', this.transactionId);
        return this.diagnosticOrderService.getTemplates({OrganizationId: this.orgObj.orgId}).toPromise()
          .then(res => {
            this.logService.info(`[app.js] Getting Diagnostic Order Templates Done`, '[app.js]', this.transactionId);
            if (res.json().length === 1)
              return {
                path: 'Pathologist/order/:patientId',
                params: {patientId: this.patientId, testName: res.body[0].name}
              };
            else
              return {path: 'Pathologist/order/:patientId', params: {patientId: this.patientId}};
          });
      });
  }

  getRedirectStateByPatient(appConfiguration) {
    let emrIntegration = this.routerManagerService.queryParameters?.EMRIntegration;
    let patientDashboardState = {
      path: 'dashboard/:type/:patientId',
      params: {
        type: 'patient_dashboard_tests',
        patientId: this.patientId,
        showWarning: false,
        transactionId: this.transactionId
      }
    }

    this.logService.info(`[app.js] redirecting to Dashboard Component`, '[app.js]', this.transactionId);

    if (emrIntegration) {
      patientDashboardState.params['EMRIntegration'] = emrIntegration;
    }

    if (this.appName) {
      patientDashboardState.params['appname'] = this.appName;

      if (appConfiguration) {
        if (appConfiguration.enable) {
          if (Array.isArray(appConfiguration.items) && appConfiguration.items.length > 0 && appConfiguration.items[0].patientRedirectUrl) {
            let redirectUrl = appConfiguration.items[0].patientRedirectUrl;
            let uriState = this.routerManagerService.getStateToGo(redirectUrl, {rowData: {data: {id: this.patientId}}}, null, this.appName);
            patientDashboardState = {path: uriState.name, params: uriState.queryParams};

            if (emrIntegration) {
              patientDashboardState.params['EMRIntegration'] = emrIntegration;
            }

            if (this.appName) {
              patientDashboardState.params['appname'] = this.appName;
            }
          }
        } else {
          return Promise.resolve({path: 'generic-error', params: {errorType: 'PERMISSION_DENIED'}});
        }
      }
    }

    return Promise.resolve(patientDashboardState);
  }

  configureRoute(routeState: any){
    let url = routeState.path;
    let queryParams = {};
    for (let param of Object.keys(routeState.params)){
      if (url.indexOf(":" + param) !== -1){
        url = url.replace(":"+param, routeState.params[param]);
      } else{
        queryParams[param] = routeState.params[param];
      }
    }
    return {url: url, queryParams: queryParams};
  }

}

@Injectable({providedIn:'root'})
export class validateStateFromIAM {
  constructor(private authenticationService: AuthenticationService,
              private endpointService: EndpointService,
              private cryptoService: CryptoService,
              private _http: HttpInterceptorService,
              private router: Router) {

  }

  resolve(){
    let url = new URL(window.location.href);
    let searchParams = new URLSearchParams(url.search);
    return new Promise(resolve=> {
      const usr = this.authenticationService.getUser();
      const hash = (usr && usr.permissions) ? this.cryptoService.hashSha256(JSON.stringify(usr.permissions.sort()) + '_' + (usr.roleId || '')) : null;
      this._http.post('/auth/validateStateFromIAM', { params: { code: searchParams.get('code') } }, undefined, { 'aux_token': hash })
        .subscribe(
          (data: any) => {
            let user;
            if (data.hasOwnProperty('data')) {
              user = data.data;
            } 
            else if (data.hasOwnProperty('body')) {
              user = data.body;
            }

            user.localSettings = {};
            this.authenticationService.setCurrentUser(user);

            this.endpointService.getGenomicsIspmAdminEndpoint()
              .subscribe(
                (res) => {
                  const _xsrf = user.xsrf || data.xsrf;
                  const adminUrl = res.endpoint + '/admin/settings/?u-code=' + encodeURIComponent(_xsrf) + (data.permissions ? '&aux_token=' + hash : '');
                  window.open(adminUrl, '_self');
                },
                (err) => {
                  console.error("Couldn't open the new admin page - " + err);
                }
              );
          },
          (error) => {
            this.router.navigate(['login']);
          }
        );
    })
  }

}

@Injectable({providedIn:'root'})
export class TranslationResolve {
  constructor(private translationHandlerService: TranslationHandlerService) {
  }

  resolve(){
    this.translationHandlerService.queryDictionary(navigator.language || 'en-US')
  }

}
